import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Header.module.css';
import mitLogo from '../../assets/images/mit-logo.png';
import DropdownMenu from '../shared/DropdownMenu';
import DataToAiWordmark from './DataToAiWordmark';

const isExternalPath = (path) => /^https?:\/\//i.test(path);

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const researchDropdownItems = [
    { label: 'Publications', path: '/research/publications' },
    { label: 'In the News', path: '/news' },
    { label: 'Reading Group', path: '/research/reading-group' },
  ];

  const projectsDropdownItems = [
    { label: 'Open Source', path: '/projects/open-source' },
    { label: 'Other Projects', path: '/projects' },
    { label: 'Blog', path: 'https://medium.com/mit-data-to-ai-lab' },
  ];

  const labDropdownItems = [
    { label: 'People', path: '/lab/people' },
    { label: 'Joining DAI', path: '/lab/join' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Contact Us', path: '/contact' },
  ];

  const mobileNavGroups = [
    { label: 'Research', items: researchDropdownItems },
    { label: 'Projects', items: projectsDropdownItems },
    { label: 'Lab', items: labDropdownItems },
  ];

  const toggleMobileGroup = (label) => {
    setMobileOpenMenu((prev) => (prev === label ? null : label));
  };

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.leftSection}>
            <Link to="/" className={styles.logoLink} aria-label="Data to AI Lab, home">
              <DataToAiWordmark className={styles.logoWordmark} />
              <span className={styles.divider}>|</span>
              <img src={mitLogo} alt="MIT" className={styles.mitLogo} />
            </Link>
          </div>

          <nav className={styles.nav}>
            <DropdownMenu
              label="Research"
              items={researchDropdownItems}
              className={styles.navItem}
            />
            <DropdownMenu
              label="Projects"
              items={projectsDropdownItems}
              className={styles.navItem}
            />
            <DropdownMenu
              label="Lab"
              items={labDropdownItems}
              className={styles.navItem}
            />
          </nav>

          <button
            className={styles.hamburger}
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H21M3 12H21M3 18H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className={styles.mobileMenu}>
          <div className={styles.mobileMenuHeader}>
            <Link to="/" className={styles.logoLink} aria-label="Data to AI Lab, home">
              <DataToAiWordmark className={styles.logoWordmark} />
              <span className={styles.divider}>|</span>
              <img src={mitLogo} alt="MIT" className={styles.mitLogo} />
            </Link>
            <button
              className={styles.closeButton}
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <nav className={styles.mobileNav}>
            <Link to="/" className={styles.mobileNavItem}>
              Home
            </Link>

            {mobileNavGroups.map((group) => (
              <div key={group.label} className={styles.mobileNavGroup}>
                <button
                  className={styles.mobileNavItem}
                  onClick={() => toggleMobileGroup(group.label)}
                >
                  {group.label}
                  <svg
                    className={`${styles.mobileArrow} ${mobileOpenMenu === group.label ? styles.mobileArrowOpen : ''}`}
                    width="14"
                    height="14"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.25 5.25L6.5 8.5L9.75 5.25"
                      stroke="currentColor"
                      strokeWidth="1.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {mobileOpenMenu === group.label && (
                  <div className={styles.mobileSubMenu}>
                    {group.items.map((item) =>
                      isExternalPath(item.path) ? (
                        <a
                          key={item.path}
                          href={item.path}
                          className={styles.mobileSubItem}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.label}
                        </a>
                      ) : (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`${styles.mobileSubItem} ${location.pathname === item.path ? styles.mobileSubItemActive : ''}`}
                        >
                          {item.label}
                        </Link>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
